import { Stack, Typography, IconButton, MenuItem } from '@mui/material';
import Iconify from 'src/components/iconify';
import PropTypes from 'prop-types';
import { fDate } from 'src/utils/format-time';
import CustomPopover, { usePopover } from 'src/components/custom-popover';
import { useSnackbar } from 'notistack';
import {
  activateUser,
  deactivateUser,
  makeAdmin,
  resendTeamInvite,
} from 'src/services/user.service';
import { BASE_URL } from 'src/config-global';

export const UserRow = ({
  COLUMNS,
  ROLES,
  STATUS,
  user,
  orgId,
  refetchUsers,
}) => {
  // hooks
  const { id, name, email, role, status, isOwner, dateAdded, inviteLink } =
    user;
  const popover = usePopover();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const popoverItems = {
    resendInvite: {
      label: 'Resend invite',
      condition: () => status === STATUS.INVITED.value,
      onClick: () => {
        enqueueSnackbar('Resending invite...', {
          key: 'resend-invite',
          persist: true,
        });

        resendTeamInvite(orgId, id)
          .then(response => {
            if (response.success) {
              closeSnackbar('resend-invite');
              enqueueSnackbar('Invite resent successfully');
            } else throw new Error('Failed to resend invite');
          })
          .catch(err => {
            enqueueSnackbar('Failed to resend invite', { variant: 'error' });
          })
          .finally(() => {
            closeSnackbar('resend-invite');
            refetchUsers();
          });
      },
    },
    setAsAdmin: {
      label: 'Set as Admin',
      condition: () => {
        if (role === ROLES.MEMBER.value && status === STATUS.ACTIVE.value) {
          return true;
        }
        return false;
      },
      onClick: () => {
        enqueueSnackbar('Making user admin...', {
          key: 'make-admin',
          persist: true,
        });

        makeAdmin(orgId, id)
          .then(response => {
            if (response.success) {
              closeSnackbar('make-admin');
              enqueueSnackbar('User made admin successfully');
            }
          })
          .catch(err => {
            enqueueSnackbar('Failed to make user admin', { variant: 'error' });
          })
          .finally(() => {
            closeSnackbar('make-admin');
            refetchUsers();
          });
      },
    },
    deactivate: {
      label: 'Deactivate',
      condition: () => {
        if (status === STATUS.ACTIVE.value) {
          return true;
        }
        return false;
      },
      onClick: () => {
        enqueueSnackbar('Deactivating user...', {
          key: 'deactivate-user',
          persist: true,
        });

        deactivateUser(orgId, id)
          .then(response => {
            if (response.success) {
              closeSnackbar('deactivate-user');
              enqueueSnackbar('User deactivated successfully');
            }
          })
          .catch(err => {
            enqueueSnackbar('Failed to deactivate user', { variant: 'error' });
          })
          .finally(() => {
            closeSnackbar('deactivate-user');
            refetchUsers();
          });
      },
    },
    setAsActive: {
      label: 'Set as Active',
      condition: () => {
        if (status === STATUS.INACTIVE.value) {
          return true;
        }
        return false;
      },
      onClick: () => {
        enqueueSnackbar('Activating user...', {
          key: 'activate-user',
          persist: true,
        });

        activateUser(orgId, id)
          .then(response => {
            if (response.success) {
              closeSnackbar('activate-user');
              enqueueSnackbar('User activated successfully');
            }
          })
          .catch(err => {
            enqueueSnackbar('Failed to activate user', { variant: 'error' });
          })
          .finally(() => {
            closeSnackbar('activate-user');
            refetchUsers();
          });
      },
    },
    copyInviteLink: {
      label: 'Copy invite link',
      condition: () => status === STATUS.INVITED.value && inviteLink,
      onClick: () => {
        navigator.clipboard.writeText(BASE_URL + inviteLink);
        enqueueSnackbar('Copied');
      },
    },
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing="16px"
        bgcolor="background.primaryWhite1"
        py="20.5px"
        px="20px"
        borderRadius="8px"
        border={theme => `1px solid ${theme.palette.border.primaryBlack3}`}
      >
        <Stack
          width={COLUMNS.name.width}
          alignItems="center"
          direction="row"
          spacing="8px"
        >
          <Typography
            variant="input"
            sx={{
              whiteSpace: 'nowrap',
              lineHeight: 'normal',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name || '-'}
          </Typography>
          {isOwner && (
            <Stack
              px="6px"
              py="2px"
              borderRadius="6px"
              alignItems="center"
              justifyContent="center"
              bgcolor="background.primaryAccent2"
              color="content.accent1"
              width="min-content"
              fontSize={10}
            >
              Owner
            </Stack>
          )}
        </Stack>

        <Typography
          variant="input"
          width={COLUMNS.email.width}
          sx={{
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {email}
        </Typography>

        <Typography
          variant="input"
          width={COLUMNS.role.width}
          sx={{
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {ROLES[role].label}
        </Typography>

        <Typography
          variant="input"
          width={COLUMNS.status.width}
          sx={{
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {STATUS[status].label}
        </Typography>

        <Typography
          variant="input"
          width={COLUMNS.dateAdded.width}
          sx={{
            lineHeight: 'normal',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {fDate(dateAdded, 'do MMM')}
        </Typography>
        <Stack width={COLUMNS.actions.width}>
          {!isOwner && (
            <IconButton
              onClick={popover.onOpen}
              sx={{
                width: 'min-content',
              }}
            >
              <Iconify icon="tabler:dots-vertical" />
            </IconButton>
          )}
        </Stack>
      </Stack>
      <CustomPopover
        open={popover.open}
        onClose={() => {
          popover.onClose();
        }}
      >
        {Object.keys(popoverItems)
          .filter(item => popoverItems[item].condition())
          .map(item => (
            <MenuItem
              key={item}
              onClick={() => {
                popoverItems[item].onClick();
                popover.onClose();
              }}
            >
              {popoverItems[item].label}
            </MenuItem>
          ))}
      </CustomPopover>
    </>
  );
};

UserRow.propTypes = {
  user: PropTypes.object.isRequired,
  COLUMNS: PropTypes.object.isRequired,
  ROLES: PropTypes.object.isRequired,
  STATUS: PropTypes.object.isRequired,
  orgId: PropTypes.string.isRequired,
  refetchUsers: PropTypes.func.isRequired,
};
